import type { Money } from './types';

export function currencyDigits(currency: string): number {
  if (currency === 'UNKNOWN') return 2;
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits; } catch { return 2; }
}

function plainDecimal(value: string): string | null {
  if (/^-?\d+(\.\d+)?$/.test(value)) return value;
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(value);
  if (!match) return null;
  const [, sign, integer, fraction = '', exponentValue] = match;
  const exponent = Number(exponentValue);
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 1000) return null;
  const digits = `${integer}${fraction}`;
  const position = integer.length + exponent;
  if (position <= 0) return `${sign}0.${'0'.repeat(-position)}${digits}`;
  if (position >= digits.length) return `${sign}${digits}${'0'.repeat(position - digits.length)}`;
  return `${sign}${digits.slice(0, position)}.${digits.slice(position)}`;
}

function roundDecimal(value: string, digits: number): string {
  const negative = value.startsWith('-');
  const [integerPart, fractionPart = ''] = (negative ? value.slice(1) : value).split('.');
  if (digits === 0) {
    const rounded = fractionPart[0] && fractionPart[0] >= '5' ? addOne(integerPart) : integerPart;
    return `${negative && rounded !== '0' ? '-' : ''}${rounded}`;
  }
  const kept = fractionPart.slice(0, digits).padEnd(digits, '0');
  if (fractionPart.length <= digits || fractionPart[digits] < '5') return `${negative ? '-' : ''}${integerPart}.${kept}`;
  const incremented = addOne(`${integerPart}${kept}`);
  const splitAt = incremented.length - digits;
  return `${negative ? '-' : ''}${incremented.slice(0, splitAt) || '0'}.${incremented.slice(splitAt)}`;
}

function addOne(value: string): string {
  const digits = value.split('');
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] !== '9') { digits[index] = String(Number(digits[index]) + 1); return digits.join(''); }
    digits[index] = '0';
  }
  return `1${digits.join('')}`;
}

export function formatMoney(value: Money, currency: string): string {
  if (value === null) return 'Unavailable';
  const decimal = plainDecimal(value);
  if (!decimal) return 'Unavailable';
  const rounded = roundDecimal(decimal, currencyDigits(currency));
  const negative = rounded.startsWith('-');
  const [integer, fraction] = (negative ? rounded.slice(1) : rounded).split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const zero = /^0(?:\.0+)?$/.test(negative ? rounded.slice(1) : rounded);
  return `${negative && !zero ? '−' : ''}${currency === 'UNKNOWN' ? 'Unknown currency' : currency} ${grouped}${fraction === undefined ? '' : `.${fraction}`}`;
}

export function formatCount(value: number): string { return new Intl.NumberFormat().format(value); }

/** Exact monetary subtraction without converting authoritative decimals to binary floats. */
export function decimalDifference(current: Money, previous: Money): Money {
  if (current === null || previous === null) return null;
  const a = plainDecimal(current); const b = plainDecimal(previous);
  if (a === null || b === null) return null;
  const scale = Math.max(a.split('.')[1]?.length || 0, b.split('.')[1]?.length || 0);
  const parts = (value: string) => {
    const negative = value.startsWith('-');
    const [integer, fraction = ''] = (negative ? value.slice(1) : value).split('.');
    const digits = `${integer}${fraction.padEnd(scale, '0')}`.replace(/^0+(?=\d)/, '');
    return { negative, digits };
  };
  const left = parts(a); const right = parts(b);
  const compare = left.digits.length === right.digits.length
    ? left.digits.localeCompare(right.digits) : left.digits.length - right.digits.length;
  const adding = left.negative !== right.negative;
  const greater = compare >= 0 ? left.digits : right.digits;
  const lesser = compare >= 0 ? right.digits : left.digits;
  const negative = adding ? left.negative : compare >= 0 ? left.negative : !left.negative;
  const width = Math.max(left.digits.length, right.digits.length);
  const x = (adding ? left.digits : greater).padStart(width, '0');
  const y = (adding ? right.digits : lesser).padStart(width, '0');
  let carry = 0; let result = '';
  for (let index = width - 1; index >= 0; index--) {
    const value = adding ? Number(x[index]) + Number(y[index]) + carry : Number(x[index]) - Number(y[index]) - carry;
    if (adding) { result = String(value % 10) + result; carry = Math.floor(value / 10); }
    else { result = String(value < 0 ? value + 10 : value) + result; carry = value < 0 ? 1 : 0; }
  }
  if (adding && carry) result = String(carry) + result;
  result = result.replace(/^0+(?=\d)/, '').padStart(scale + 1, '0');
  const zero = !/[1-9]/.test(result);
  const formatted = scale ? `${result.slice(0, -scale)}.${result.slice(-scale)}` : result;
  return `${negative && !zero ? '-' : ''}${formatted}`;
}

export function formatDateTime(value: string | null): string {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not recorded' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Hong_Kong' }).format(parsed);
}
